import { useEffect, useRef, useState } from "react";
import Joyride from "react-joyride";
import { useJoyrideStyles } from "../../theme";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { TRYON_SERVER_URL } from "../../config";
import { selectAccessToken, selectAuthenticated, selectUserData } from "../../selectors";
import { fetchAllImages } from "../../Utils/ExperimentImageUtils";
import { fetchAllResults, fetchExperiment } from "../../Utils/ExperimentUtils";
import { generateVirtualTryOn, tryonCredits, TRYON_MAX_PIECES } from "../../Utils/VTONUtils";
import UploadImage from "../../components/UploadImage";
import SignInPrompt from "../../components/SignInPrompt";
import PickerSheet from "./components/PickerSheet";
import Stage from "./components/Stage";
import { mediaUrl, usableImages } from "./media";
import Photo from "./components/Photo";
import { ShirtIcon, UserIcon, XIcon } from "lucide-react";
import { TOURS, TOUR_PROPS, useTour } from "../../Utils/tours";

const TOUR_STYLES = {
    options: {
        arrowColor: "#ffffff",
        backgroundColor: "#ffffff",
        primaryColor: "#BE3120",
        textColor: "#0F0134",
        zIndex: 10000,
    },
    tooltip: { borderRadius: 16, fontFamily: "Mulish, sans-serif", padding: 16 },
    tooltipContent: { fontSize: 14, padding: "8px 0 0" },
    buttonNext: { borderRadius: 999, fontFamily: "Mulish, sans-serif", fontWeight: 600 },
    buttonBack: { color: "#BE3120", fontFamily: "Mulish, sans-serif" },
    buttonSkip: { color: "#41424C", fontFamily: "Mulish, sans-serif" },
};

const tourSteps = [
    {
        target: ".vton-photo",
        content: "Choose a photo of the person, or upload one.",
        disableBeacon: true,
    },
    {
        target: ".vton-garments",
        content: "Add the pieces for this look.",
        disableBeacon: true,
    },
    {
        target: ".vton-try",
        content: "Try On starts the look. The button shows the credits it will use.",
        disableBeacon: true,
    },
    {
        target: ".vton-earlier",
        content: "Earlier keeps past try-ons, ready to open, download, or share.",
        disableBeacon: true,
    },
];

function storedParams(result) {
    if (!result?.params) return {};
    if (typeof result.params === "object") return result.params;
    try {
        const parsed = JSON.parse(result.params);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (parseError) {
        return {};
    }
}

function catalogMatch(person, pieces, catalog) {
    if (!person || pieces.length !== 1) return null;
    const garment = pieces[0];
    if (person.access !== "public" || garment.access !== "public") return null;
    return catalog.find((item) =>
        item.status === "completed"
        && String(item.model?.id) === String(person.id)
        && String(item.garment?.id) === String(garment.id)
        && item.result?.image_url
    ) || null;
}

function piecesFromResult(result, library) {
    if (result.lookPieces?.length) return result.lookPieces;
    const ids = Array.isArray(storedParams(result).garment_ids) ? storedParams(result).garment_ids.map(String) : [];
    if (ids.length && library.length) {
        const matched = ids.map((id) => library.find((item) => String(item.id) === id)).filter(Boolean);
        if (matched.length) return matched;
    }
    return result.garment ? [result.garment] : [];
}

export default function Vton() {
    const navigate = useNavigate();
    const accessToken = useSelector(selectAccessToken) || null;
    const authenticated = useSelector(selectAuthenticated);
    const userData = JSON.parse(useSelector(selectUserData) || "null");
    const isPhone = useMediaQuery({ query: "(max-width: 760px)" });

    const [photos, setPhotos] = useState([]);
    const [garments, setGarments] = useState([]);
    const [photosLoaded, setPhotosLoaded] = useState(false);
    const [garmentsLoaded, setGarmentsLoaded] = useState(false);
    const [results, setResults] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [resultsLoaded, setResultsLoaded] = useState(false);
    const [person, setPerson] = useState(null);
    const [pieces, setPieces] = useState([]);
    const [finish, setFinish] = useState("2k");
    const [activeId, setActiveId] = useState(null);
    const [sheet, setSheet] = useState(null);
    const [uploadType, setUploadType] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [awaitingId, setAwaitingId] = useState(null);
    const waitRef = useRef(null);
    const [error, setError] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [frameHeight, setFrameHeight] = useState(null);
    const { done: tourDone, onCallback: onTourCallback } = useTour(TOURS.VTON);
    const tourStyles = useJoyrideStyles(TOUR_STYLES);

    useEffect(() => {
        const measure = () => {
            const nav = document.getElementById("navbar");
            const top = nav ? nav.getBoundingClientRect().bottom : 0;
            setFrameHeight(Math.max(0, Math.round(window.innerHeight - top)));
        };
        measure();
        const nav = document.getElementById("navbar");
        const observer = nav ? new ResizeObserver(measure) : null;
        if (nav) observer.observe(nav);
        window.addEventListener("resize", measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, []);

    const active = results.find((item) => item.id === activeId)
        || catalog.find((item) => item.id === activeId)
        || null;
    const visibleResults = results.filter((item) => item.status !== "failed");

    useEffect(() => {
        const baseUrl = `${TRYON_SERVER_URL}/api/v1/experiment_image/?`;
        let cancelled = false;
        fetchAllImages(baseUrl, accessToken, true, true, { type: "model" }).then((response) => {
            if (cancelled) return;
            if (response.statusCode === 200) setPhotos(usableImages(response.data));
            setPhotosLoaded(true);
        });
        fetchAllImages(baseUrl, accessToken, true, true, { type: "garment" }).then((response) => {
            if (cancelled) return;
            if (response.statusCode === 200) setGarments(usableImages(response.data));
            setGarmentsLoaded(true);
        });
        return () => { cancelled = true; };
    }, [accessToken]);

    useEffect(() => {
        if (authenticated !== true) {
            if (authenticated === false) setResultsLoaded(true);
            return;
        }
        let cancelled = false;
        fetchAllResults(accessToken, "virtual_try_on").then((response) => {
            if (cancelled) return;
            if (response.statusCode === 200) {
                const rows = Array.isArray(response.data) ? response.data : [];
                setResults(rows.filter((item) => item.access !== "public"));
            }
            setResultsLoaded(true);
        });
        return () => { cancelled = true; };
    }, [accessToken, authenticated]);

    useEffect(() => {
        let cancelled = false;
        fetchAllResults(null, "virtual_try_on").then((response) => {
            if (cancelled || response.statusCode !== 200) return;
            const rows = Array.isArray(response.data) ? response.data : [];
            setCatalog(rows.filter((item) => item.access === "public" && item.status === "completed" && item.result?.image_url));
        });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (generating || awaitingId) return;
        const match = catalogMatch(person, pieces, catalog);
        if (match) {
            setActiveId((current) => current === match.id ? current : match.id);
            return;
        }
        setActiveId((current) => catalog.some((item) => item.id === current) ? null : current);
    }, [person, pieces, catalog, generating, awaitingId]);

    useEffect(() => {
        if (!garmentsLoaded || garments.length === 0) return;
        setResults((current) => {
            let changed = false;
            const next = current.map((item) => {
                if (item.lookPieces?.length) return item;
                const ids = Array.isArray(storedParams(item).garment_ids) ? storedParams(item).garment_ids : [];
                if (ids.length < 2) return item;
                const remembered = piecesFromResult(item, garments);
                if (remembered.length < 2) return item;
                changed = true;
                return { ...item, lookPieces: remembered };
            });
            return changed ? next : current;
        });
    }, [garmentsLoaded, garments]);

    useEffect(() => {
        if (!active || (active.status !== "pending" && active.status !== "running")) return undefined;
        const timer = setInterval(async () => {
            const response = await fetchExperiment(active.id, accessToken);
            if (response.status !== "success" || !response.data) return;
            setResults((current) => current.map((item) => item.id === response.data.id
                ? { ...item, ...response.data, lookPieces: item.lookPieces }
                : item));
        }, 4000);
        return () => clearInterval(timer);
    }, [active, accessToken]);

    useEffect(() => {
        if (!awaitingId) return;
        const row = results.find((item) => item.id === awaitingId);
        if (row && (row.status === "completed" || row.status === "failed")) setAwaitingId(null);
    }, [awaitingId, results]);

    const waiting = generating || Boolean(awaitingId);

    useEffect(() => {
        if (!waiting) return undefined;
        waitRef.current?.focus();
        function blockEscape(event) {
            if (event.key === "Escape") event.preventDefault();
        }
        document.addEventListener("keydown", blockEscape);
        return () => document.removeEventListener("keydown", blockEscape);
    }, [waiting]);

    function openUpload(type) {
        if (authenticated !== true) {
            navigate("/signin");
            return;
        }
        setUploadType(type);
    }

    function acceptUpload(next) {
        const added = next?.[0];
        if (uploadType === "model") {
            setPhotos(next);
            if (added) setPerson(added);
            setSheet(null);
        } else if (uploadType === "garment") {
            setGarments(next);
            if (added && !pieces.some((item) => item.id === added.id)) {
                if (pieces.length >= TRYON_MAX_PIECES) {
                    setError("A look can include 4 pieces.");
                } else {
                    setPieces((current) => current.concat(added));
                    setActiveId(null);
                }
            }
        }
        setUploadType(null);
    }

    async function tryOn() {
        const saved = catalogMatch(person, pieces, catalog);
        if (saved) {
            setActiveId(saved.id);
            setError("");
            return;
        }
        if (authenticated !== true) {
            navigate("/signin");
            return;
        }
        if (active || !person || pieces.length === 0 || generating) return;
        if (pieces.length > TRYON_MAX_PIECES) {
            setError("A look can include 4 pieces.");
            return;
        }
        const balance = Number(userData?.credits);
        if (Number.isFinite(balance) && balance < credits) {
            navigate(`/credits?buy_credits=true&num_of_credits=${credits}`);
            return;
        }
        setGenerating(true);
        setError("");
        const response = await generateVirtualTryOn({
            person_id: person.id,
            garment_ids: pieces.map((item) => item.id),
            resolution: requestedFinish,
            accessToken,
        });
        if (response.status !== "success" || !response.data) {
            setGenerating(false);
            setError(response.message || "Could not start the try-on.");
            return;
        }
        const experiment = { ...response.data, lookPieces: pieces };
        setAwaitingId(experiment.id);
        setResults((current) => [experiment, ...current.filter((item) => item.id !== experiment.id)]);
        setActiveId(experiment.id);
        setGenerating(false);
    }

    async function downloadResult() {
        const path = active?.result?.image_url;
        if (!path) return;
        const response = await fetch(mediaUrl(path));
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "try-on.png";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

    async function shareResult() {
        const path = active?.result?.image_url;
        if (!path) return;
        const url = mediaUrl(path);
        if (navigator.share) {
            try { await navigator.share({ title: "Try-on", url }); } catch (shareError) { /* dismissed */ }
            return;
        }
        await navigator.clipboard.writeText(url);
    }

    async function sendFeedback(value) {
        if (!active || !accessToken) return;
        const response = await fetch(`${TRYON_SERVER_URL}/api/v1/experiment/${active.id}/`, {
            method: "PATCH",
            headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
            body: JSON.stringify({ user_feedback: value }),
        });
        if (!response.ok) {
            setError("Could not save that reaction.");
            return;
        }
        setResults((current) => current.map((item) => item.id === active.id ? { ...item, user_feedback: value } : item));
    }

    async function deleteResult() {
        if (!active) return;
        const response = await fetch(`${TRYON_SERVER_URL}/api/v1/experiment/${active.id}/`, {
            method: "DELETE",
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });
        if (!response.ok) {
            setError("Could not delete that try-on.");
            setConfirmDelete(false);
            return;
        }
        setResults((current) => current.filter((item) => item.id !== active.id));
        setActiveId(null);
        setConfirmDelete(false);
    }

    function showResult(result) {
        const remembered = piecesFromResult(result, garments);
        setActiveId(result.id);
        if (result.model) setPerson(result.model);
        setPieces(remembered);
        const size = String(storedParams(result).resolution || "").toLowerCase();
        if (remembered.length < 2 && (size === "2k" || size === "4k")) setFinish(size);
        if (remembered.length) {
            setResults((current) => current.map((item) => item.id === result.id ? { ...item, lookPieces: remembered } : item));
        }
        setSheet(null);
    }

    function clearStage() {
        setPerson(null);
        setPieces([]);
        setActiveId(null);
        setError("");
        setConfirmDelete(false);
    }

    const requestedFinish = pieces.length > 1 ? "2k" : finish;
    const credits = tryonCredits(pieces.length || 1, requestedFinish);
    const ready = Boolean(person) && pieces.length > 0;
    const viewing = Boolean(active);
    const savedLook = Boolean(catalogMatch(person, pieces, catalog));
    const canTry = !viewing && ready && !waiting && !savedLook;
    const occupied = viewing || Boolean(person) || pieces.length > 0;
    const tryLabel = waiting ? "Trying on…" : savedLook ? "Try On" : `Try On (${credits} credits)`;

    return (
        <div
            className={`flex w-full flex-col gap-4 overflow-hidden bg-ground selection:bg-primary-100 ${isPhone ? "px-4 pt-4 pb-[max(0.5rem,env(safe-area-inset-bottom))]" : "p-4"}`}
            style={{ height: frameHeight == null ? "calc(100dvh - 5rem)" : `${frameHeight}px` }}
        >
            <div className={`flex min-h-0 flex-1 gap-4 ${isPhone ? "flex-col" : ""}`}>
                <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
                    <Stage
                        isPhone={isPhone}
                        person={person}
                        pieces={pieces}
                        result={active}
                        generating={generating}
                        earlier={isPhone ? (
                            <button type="button" onClick={() => setSheet("earlier")} className="vton-earlier shrink-0 font-Mulish text-sm font-semibold text-primary-700">
                                {visibleResults.length ? `Earlier · ${visibleResults.length}` : "Earlier"}
                            </button>
                        ) : null}
                        onOpenPhoto={() => setSheet("photo")}
                        onOpenPieces={() => setSheet("garments")}
                        onRemovePiece={(id) => { setPieces((current) => current.filter((item) => item.id !== id)); setActiveId(null); }}
                        onDownload={downloadResult}
                        onShare={shareResult}
                        onLike={() => sendFeedback("positive")}
                        onDislike={() => sendFeedback("negative")}
                        onDelete={() => setConfirmDelete(true)}
                        canAddPiece={pieces.length < TRYON_MAX_PIECES}
                    />
                    <div className={`flex shrink-0 gap-2 ${isPhone ? "flex-col" : "flex-row items-center"}`}>
                        <div className="flex gap-2">
                            <DockButton className="vton-photo" icon={UserIcon} onClick={() => setSheet("photo")}>Photo</DockButton>
                            <DockButton className="vton-garments" icon={ShirtIcon} onClick={() => setSheet("garments")}>Garments</DockButton>
                            {occupied ? <DockButton icon={XIcon} onClick={clearStage}>Clear</DockButton> : null}
                        </div>
                        {pieces.length < 2 ? (
                            <div className={`flex items-center gap-4 ${isPhone ? "" : "ml-auto"}`} role="tablist" aria-label="Finish">
                                {["2k", "4k"].map((size) => (
                                    <button
                                        key={size}
                                        type="button"
                                        role="tab"
                                        aria-selected={finish === size}
                                        onClick={() => { setFinish(size); setActiveId(null); }}
                                        className={`border-b-2 pb-0.5 font-Mulish text-sm ${finish === size ? "border-primary-500 font-semibold text-secondary" : "border-transparent text-graphite"}`}
                                    >
                                        {size.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                        <button
                            type="button"
                            onClick={tryOn}
                            disabled={(authenticated === true && !canTry) || savedLook}
                            className={`vton-try rounded-full px-4 py-2.5 font-Mulish text-sm font-semibold ${isPhone ? "w-full" : pieces.length < 2 ? "" : "ml-auto"} ${(authenticated !== true && !savedLook) || canTry ? "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]" : "cursor-not-allowed bg-primary-100 text-primary-800"}`}
                        >
                            {tryLabel}
                        </button>
                    </div>
                    {error ? (
                        <p className="rounded-2xl bg-white px-3 py-2 font-Mulish text-sm text-graphite shadow-[0_0_0_1px_var(--hairline)]">{error}</p>
                    ) : null}
                </div>

                {isPhone ? null : (
                    <aside className="vton-earlier flex w-[188px] shrink-0 flex-col rounded-2xl bg-white p-3 shadow-[0_0_0_1px_var(--hairline)]">
                        <h2 className="mb-3 font-Mulish text-base font-semibold text-secondary">Earlier</h2>
                        <EarlierList
                            authenticated={authenticated}
                            loaded={resultsLoaded}
                            results={visibleResults}
                            activeId={activeId}
                            onShow={showResult}
                            onSignIn={() => navigate("/signin")}
                        />
                    </aside>
                )}
            </div>

            {sheet === "photo" ? (
                <PickerSheet
                    mode="single"
                    title="Choose photo"
                    note="One person for this look."
                    images={photos}
                    selectedIds={person ? [person] : []}
                    loading={!photosLoaded}
                    onClose={() => setSheet(null)}
                    onConfirm={(chosen) => { setPerson(chosen[0] || null); setActiveId(null); setSheet(null); }}
                    onUpload={() => openUpload("model")}
                />
            ) : null}
            {sheet === "garments" ? (
                <PickerSheet
                    mode="many"
                    title="Choose garments"
                    note="Add every piece for this look. Up to 4."
                    images={garments}
                    selectedIds={pieces}
                    max={TRYON_MAX_PIECES}
                    loading={!garmentsLoaded}
                    onClose={() => setSheet(null)}
                    onConfirm={(chosen) => { setPieces(chosen); setActiveId(null); setSheet(null); }}
                    onUpload={() => openUpload("garment")}
                />
            ) : null}
            {sheet === "earlier" ? (
                <div className="fixed inset-0 z-50 flex flex-col bg-white" role="dialog" aria-modal="true" aria-label="Earlier">
                    <div className="flex items-center gap-3 border-b border-hairline px-4 py-3">
                        <h2 className="flex-1 font-Mulish text-lg font-semibold text-secondary">Earlier</h2>
                        <button type="button" aria-label="Close" onClick={() => setSheet(null)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary-700 shadow-[0_0_0_1px_var(--hairline)]">×</button>
                    </div>
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
                        <EarlierList
                            authenticated={authenticated}
                            loaded={resultsLoaded}
                            results={visibleResults}
                            activeId={activeId}
                            onShow={showResult}
                            onSignIn={() => navigate("/signin")}
                            grid
                        />
                    </div>
                </div>
            ) : null}

            {uploadType ? (
                <UploadImage
                    imageType={uploadType === "model" ? "model" : "garment"}
                    allImages={uploadType === "model" ? photos : garments}
                    setAllImages={acceptUpload}
                    closeModal={() => setUploadType(null)}
                />
            ) : null}

            {!tourDone && !waiting ? (
                <Joyride {...TOUR_PROPS} steps={tourSteps} callback={onTourCallback} styles={tourStyles} />
            ) : null}

            {waiting ? (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-overlay p-4">
                    <div
                        ref={waitRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="vton-wait-title"
                        aria-describedby="vton-wait-note"
                        tabIndex={-1}
                        className="w-full max-w-sm rounded-2xl bg-white px-6 py-8 text-center shadow-[0_0_0_1px_var(--hairline)] outline-none"
                    >
                        <Spinner className="mx-auto h-8 w-8" />
                        <p id="vton-wait-title" className="mt-4 font-Mulish text-lg font-semibold text-secondary">Trying on your look</p>
                        <p id="vton-wait-note" className="mt-1 font-Mulish text-sm text-graphite">Please wait while we perform the virtual try-on.</p>
                    </div>
                </div>
            ) : null}

            {confirmDelete ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4">
                    <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-[0_0_0_1px_var(--hairline)]">
                        <p className="font-Mulish text-lg font-semibold text-secondary">Delete this try-on?</p>
                        <p className="mt-1 font-Mulish text-sm text-graphite">It will leave your Earlier list.</p>
                        <div className="mt-4 flex justify-end gap-2">
                            <button type="button" onClick={() => setConfirmDelete(false)} className="rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700">Keep</button>
                            <button type="button" onClick={deleteResult} className="rounded-full bg-primary-700 px-4 py-2 font-Mulish text-sm font-semibold text-white">Delete</button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function Spinner({ className = "" }) {
    return <span className={`inline-block animate-spin rounded-full border-2 border-primary-200 border-t-primary-700 ${className}`} aria-hidden="true" />;
}

function DockButton({ icon: Glyph, children, onClick, className = "" }) {
    return (
        <button type="button" onClick={onClick} className={`inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm font-semibold text-primary-700 hover:bg-primary-50 md:flex-none ${className}`}>
            <Glyph className="h-4 w-4 shrink-0" aria-hidden="true" />
            {children}
        </button>
    );
}

function EarlierList({ authenticated, loaded, results, activeId, onShow, onSignIn, grid }) {
    if (authenticated === false) {
        return (
            <SignInPrompt
                title="Sign in to see your try-ons"
                message="Your try-ons will show up here."
                onSignIn={onSignIn}
                className="h-full px-3 py-6"
            />
        );
    }
    if (authenticated == null || !loaded) {
        return (
            <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-3 py-8" role="status" aria-live="polite">
                <Spinner className="h-6 w-6" />
                <p className="font-Mulish text-sm text-graphite">Loading try-ons</p>
            </div>
        );
    }
    if (results.length === 0) {
        return <p className="font-Mulish text-sm text-graphite">No try-ons yet</p>;
    }
    return (
        <div className={grid ? "grid grid-cols-3 gap-3" : "flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-0.5"}>
            {results.map((result) => {
                const selected = result.id === activeId;
                const src = result?.result?.image_url || result?.model?.image_url;
                return (
                    <button
                        key={result.id}
                        type="button"
                        aria-label="Open try-on"
                        onClick={() => onShow(result)}
                        className={`relative block aspect-[3/4] w-full shrink-0 overflow-hidden rounded-lg border-2 bg-placeholder ${selected ? "border-primary-500" : "border-hairline"}`}
                    >
                        {src ? (
                            <Photo src={mediaUrl(src)} fill className={result?.result?.image_url ? "" : "opacity-70"} />
                        ) : (
                            <span className="vton-wave absolute inset-0" aria-hidden="true" />
                        )}
                    </button>
                );
            })}
        </div>
    );
}
